import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { exchangeKakaoCode, consumeKakaoAuthState } from "utils/kakaoAuth";
import { runYmydSignupFlow } from "./ymydSignupFlow";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import Button from "components/atoms/Button";
import { log, LogLevel } from "utils/LogUtil";

/**
 * 카카오 OAuth 인가 콜백 페이지
 *
 * 흐름:
 * 1. 카카오가 ?code=...&state=... 로 redirect
 * 2. sessionStorage의 expectedState와 비교 (CSRF 방지)
 * 3. 백엔드 /api/v1/auth/kakao/exchange 로 code 전송 → kakaoId 수신
 * 4. runYmydSignupFlow 호출 → 약문약답 검증 → /signup/ymyd 또는 모달 표시
 *
 * 라우트: /auth/kakao/callback (App.tsx의 AppInsider 내부 Routes)
 */
const KakaoCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // useEffect 이중 실행 방지 (StrictMode 대비 + code 일회성 보호)
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const code = searchParams.get("code");
    const stateParam = searchParams.get("state");
    const errorParam = searchParams.get("error");
    const { state: expectedState } = consumeKakaoAuthState();

    log(LogLevel.INFO, "[KakaoCallback] 진입", {
      hasCode: !!code,
      stateParam,
      expectedState,
      stateMatch: stateParam === expectedState,
      errorParam,
    });

    // 사용자가 카카오 동의 화면에서 취소한 경우
    if (errorParam) {
      log(LogLevel.WARNING, "카카오 인증 거부:", errorParam);
      navigate("/login");
      return;
    }

    // CSRF 방지 — state 불일치 또는 code 누락
    if (!code || !stateParam || stateParam !== expectedState) {
      log(LogLevel.ERROR, "카카오 state 불일치 또는 code 누락");
      ConfirmManager.show({
        title: "오류",
        body: "카카오 인증에 실패했습니다.",
        buttons: [
          <Button
            key="ok"
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            style={{ width: "100%" }}
            onClick={() => ConfirmManager.hide()}
          >
            확인
          </Button>,
        ],
      });
      navigate("/login");
      return;
    }

    (async () => {
      try {
        WaitingManager.show({ message: "카카오 인증 처리 중..." });
        const { kakaoId } = await exchangeKakaoCode(code);
        WaitingManager.show({ message: "약문약답 계정 확인 중..." });
        await runYmydSignupFlow(kakaoId, navigate, "signup-ymyd");
      } catch (e: any) {
        WaitingManager.hide();
        log(LogLevel.ERROR, "카카오 콜백 처리 실패:", e);
        ConfirmManager.show({
          title: "오류",
          body:
            e?.message ||
            "인증 처리 중 문제가 발생했습니다. 잠시 후 다시 시도해주세요.",
          buttons: [
            <Button
              key="ok"
              type="Text"
              size="Medium"
              variant="Contained"
              color="Primary"
              style={{ width: "100%" }}
              onClick={() => {
                ConfirmManager.hide();
                navigate("/login");
              }}
            >
              확인
            </Button>,
          ],
        });
      }
    })();
  }, [searchParams, navigate]);

  return null;
};

export default KakaoCallback;
